import BigNumber from 'bignumber.js';
import { defineStore } from 'pinia';
import { useCurrencyConverter } from '~/composables/useCurrencyConverter';
import {
  ETransactionStatus,
  type ISsTransactionBets,
  type ISsTransactionBonuses,
  type ISsTransactionPayments,
} from '~/types/softswiss/ssTransactionHistory';
import { useStoreCurrencyMenu } from '../strapi/currencyMenu';
import { softswiss } from '../transport/softswiss';
import type { IRequestOptions } from '~/types/transport/requestOptions';

export const useStoreTransactionHistory = defineStore('transactionHistory', {
  state: () => ({
    _payments: [] as ISsTransactionPayments[],
    _bonuses: [] as ISsTransactionBonuses[],
    _bets: [] as ISsTransactionBets[],
    requestPromisePayments: null as Promise<any> | null,
    requestPromiseBets: null as Promise<any> | null,
    requestPromiseBonuses: null as Promise<any> | null,
    isLoadedPayments: false,
    isSortForPendingCashout: false,
  }),
  getters: {
    payments(state): ISsTransactionPayments[] {
      const storeCurrencyMenu = useStoreCurrencyMenu();
      const { convertToFormattedAmount } = useCurrencyConverter();

      return [...state._payments].reverse().map((payment) => {
        const precision =
          storeCurrencyMenu.currencies.find((c) => c.currencyCode === payment.currency)
            ?.precision || 0;

        const formated_cent = new BigNumber(payment.amount_cents || 0)
          .shiftedBy(-precision)
          .toNumber();

        const dateObj = new Date(payment.finished_at ? payment.finished_at : payment.created_at);
        const date = dateObj.toUTCString().replace('GMT', '(UTC)').split(' ').slice(1).join(' ');

        const status = payment.success
          ? ETransactionStatus.Accepted
          : payment.finished_at === null
          ? ETransactionStatus.Pending
          : ETransactionStatus.Discarded;

        const { formattedAmount, formattedCurrency } = convertToFormattedAmount(
          payment.amount_cents,
          payment.currency
        );

        return { ...payment, formated_cent, date, status, formattedAmount, formattedCurrency };
      });
    },
    bonuses(state): ISsTransactionBonuses[] {
      const { convertToFormattedAmount } = useCurrencyConverter();

      return state._bonuses.map((bonus) => {
        const dateObj = new Date(bonus.created_at);
        const date = dateObj.toUTCString().replace('GMT', '(UTC)').split(' ').slice(1).join(' ');
        const dateValidUntilObj = new Date(bonus.valid_until);
        const dateValidUntil = dateValidUntilObj
          .toUTCString()
          .replace('GMT', '(UTC)')
          .split(' ')
          .slice(1)
          .join(' ');
        const { formattedAmount, formattedCurrency } = convertToFormattedAmount(
          bonus.amount_cents,
          bonus.currency
        );

        return {
          ...bonus,
          formattedAmount,
          formattedCurrency,
          date,
          dateValidUntil,
        };
      });
    },
    bets(state): ISsTransactionBets[] {
      const { convertToFormattedAmount } = useCurrencyConverter();

      return state._bets.map((bet, index) => {
        const dateObj = new Date(bet.finished_at);
        const date = dateObj.toUTCString().replace('GMT', '(UTC)').split(' ').slice(1).join(' ');
        const formattedTotalBets = convertToFormattedAmount(bet.total_bets, bet.currency);
        const formattedTotalWins = convertToFormattedAmount(bet.total_wins, bet.currency);

        return { ...bet, date, formattedTotalBets, formattedTotalWins, id: index };
      });
    },
  },
  actions: {
    fetchData(
      storeKey: 'requestPromisePayments' | 'requestPromiseBets' | 'requestPromiseBonuses',
      path: string,
      options?: IRequestOptions
    ) {
      if (!this[storeKey]) {
        this[storeKey] = softswiss()
          .get({ path, updateInterval: 1000 * 60, ...options })
          .finally(() => (this[storeKey] = null));
      }

      return this[storeKey];
    },
    async loadPayments(options?: IRequestOptions): Promise<void> {
      const response = await this.fetchData(
        'requestPromisePayments',
        '/api/player/payments',
        options
      );

      if (response) {
        this._payments = response;
        this.isLoadedPayments = true;
      }
    },
    async loadBonuses(options?: IRequestOptions): Promise<void> {
      const response = await this.fetchData(
        'requestPromiseBonuses',
        '/api/player/bonuses',
        options
      );

      if (response) {
        this._bonuses = response;
      }
    },
    async loadBets(options?: IRequestOptions): Promise<void> {
      const response = await this.fetchData('requestPromiseBets', '/api/player/games', options);

      if (response) {
        this._bets = response;
      }
    },
    async recallTransaction(payment: ISsTransactionPayments) {
      await softswiss().post({
        path: `api/player/payments/${payment.id}/recall`,
        payload: { payment },
      });
      await this.loadPayments({ force: true });
    },
    setSortForPendingCashout(value: boolean) {
      this.isSortForPendingCashout = value;
    },
    resetState() {
      this._payments = [];
      this._bets = [];
      this._bonuses = [];
      this.isLoadedPayments = false;
    },
  },
});
